import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import "../../../styles/layouts/selectedCustomerBar/_selectedCustomerBar.scss";
import useAccount from "../../../hooks/useAccount";
import AlertService from "../../../services/alerts/alert.service";
import agentOrderActions from "../../../store/actions/agentOrder/agentOrder.actions";
import shoppingCartActions from "../../../store/actions/shoppingCart/shoppingCart.actions";

// 0017: a persistent bar ABOVE the header showing WHICH business customer an Admin/Agent is
// ordering on behalf of. It reads the SAME single source of truth the cart banner and the order's
// customerId read (state.agentOrderReducer.selectedCustomer) — no parallel state. Business users
// (always "themselves") and logged-out visitors never see it.
const ADMIN_AGENT_TYPES = ["Administrator", "Agent"];

// Matches the cart banner / dashboard badge format exactly.
const customerLabel = (customer) =>
  `${customer.firstName} ${customer.lastName} (קוד ${customer.customerId})`;

const SelectedCustomerBarComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { isLoggedIn, userInfo } = useAccount();
  const selectedCustomer = useSelector((state) => state.agentOrderReducer.selectedCustomer);
  const cartItemCount = useSelector((state) => state.shoppingCart.shoppingCartItems.length);

  const isVisible = isLoggedIn && ADMIN_AGENT_TYPES.includes(userInfo?.userType);

  // Shift the fixed header (and content) down to make room for the bar without touching the header
  // component itself — the offset lives entirely in this feature's stylesheet, keyed on a body class.
  useEffect(() => {
    document.body.classList.toggle("customer-bar-visible", isVisible);
    return () => document.body.classList.remove("customer-bar-visible");
  }, [isVisible]);

  if (!isVisible) return null;

  // 0023: open the customer-picker DIALOG over the CURRENT route (no navigation away) by adding the
  // dialog query params, preserving any other params already on the URL. The globally-mounted
  // DialogManager renders ?d=customer&action=select on any route.
  const openSelectCustomerDialog = () => {
    const next = new URLSearchParams(searchParams);
    next.set("d", "customer");
    next.set("action", "select");
    setSearchParams(next);
  };

  // D2: switching the customer while the cart still holds the CURRENT customer's items would let
  // those items be ordered for the next customer. Confirm (naming the current customer) and only on
  // confirm clear the cart, then open the picker dialog for a new customer.
  const handleChange = async () => {
    if (cartItemCount > 0) {
      const confirmed = await AlertService.confirm(
        `העגלה עדיין מלאה על הלקוח ${customerLabel(selectedCustomer)}`,
        "מעבר ללקוח אחר ירוקן את סל הקניות הנוכחי. להמשיך?",
        "רוקן והחלף לקוח",
        "השאר את הלקוח הנוכחי"
      );
      if (!confirmed) return;
      dispatch(shoppingCartActions.clearCart());
    }
    openSelectCustomerDialog();
  };

  // D3: clearing the customer with a non-empty cart — same confirm, naming the current customer.
  // On confirm clear BOTH the selection and the cart so nothing is left to leak onto another customer.
  const handleClear = async () => {
    if (cartItemCount > 0) {
      const confirmed = await AlertService.confirm(
        `העגלה עדיין מלאה על הלקוח ${customerLabel(selectedCustomer)}`,
        "ניקוי הלקוח הנבחר ירוקן גם את סל הקניות. להמשיך?",
        "נקה לקוח ורוקן עגלה",
        "ביטול"
      );
      if (!confirmed) return;
      dispatch(shoppingCartActions.clearCart());
    }
    dispatch(agentOrderActions.clearSelectedCustomer());
  };

  return (
    <div className="selectedCustomerBar" dir="rtl">
      {selectedCustomer ? (
        <>
          <div className="selectedCustomerBar__info">
            <i className="fa-solid fa-user-check"></i>
            <span className="selectedCustomerBar__label">מזמין עבור:</span>
            <span className="selectedCustomerBar__customer">{customerLabel(selectedCustomer)}</span>
          </div>
          <div className="selectedCustomerBar__actions">
            <button type="button" className="selectedCustomerBar__btn" onClick={handleChange}>
              <i className="fa-solid fa-right-left"></i> החלף לקוח
            </button>
            <button
              type="button"
              className="selectedCustomerBar__btn selectedCustomerBar__btn--clear"
              onClick={handleClear}
            >
              <i className="fa-solid fa-xmark"></i> נקה
            </button>
          </div>
        </>
      ) : (
        <div className="selectedCustomerBar__info selectedCustomerBar__info--empty">
          <i className="fa-solid fa-circle-exclamation"></i>
          <span>לא נבחר לקוח להזמנה</span>
          <button
            type="button"
            className="selectedCustomerBar__btn selectedCustomerBar__btn--cta"
            onClick={openSelectCustomerDialog}
          >
            בחר לקוח
          </button>
        </div>
      )}
    </div>
  );
};

export default SelectedCustomerBarComponent;
